/**
 * The numbers a board house gave you.
 *
 * Nine figures, and every one of them belongs to whoever is making the board
 * rather than to the drawing — which is why they are offered as named profiles
 * first and as a form second. Nobody remembers their shop's minimum annular
 * ring; they remember which shop it was.
 *
 * Choosing a profile fills the form rather than replacing it, so a set can be
 * taken as a starting point and one number changed — which is the ordinary
 * case, because a shop's published table is nearly always right except for the
 * one thing you asked them about.
 *
 * What is saved is written onto the document, so a design carries the rules it
 * was checked against wherever it goes.
 */

import {
  DEFAULT_DFM, DFM_PROFILES, allProfiles, customProfile, isCustom, sameRules,
  type DfmProfile, type DfmRules,
} from '@sircad/core/dfm/rules.ts';
import { editValue, readEdit, type DisplayUnit } from '@sircad/core/units.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface DfmRulesOptions {
  rules: DfmRules;
  units: DisplayUnit;
  /** Sets the user has saved, offered beside the ones that ship. */
  saved?: DfmProfile[];
  /** Save these onto the document and check again. */
  onApply(rules: DfmRules): void;
  /** Keep this set under a name of its own, for every board from now on. */
  onSaveProfile?(profile: DfmProfile): void;
  /** Forget a saved set. Never offered for one that ships. */
  onDeleteProfile?(id: string): void;
  /** Ask for a name. Separate so a check can answer without a browser. */
  askName?(suggested: string): string | null;
}

/** Each figure, in the order it is worth reading. */
const ROWS: { key: keyof DfmRules; label: string; hint: string }[] = [
  { key: 'minTrackWidth', label: 'Track width', hint: 'Narrowest copper line the process holds.' },
  { key: 'minClearance', label: 'Clearance', hint: 'Copper to copper, between different nets.' },
  { key: 'minAnnularRing', label: 'Annular ring', hint: 'Copper left round a hole, per side.' },
  { key: 'minDrill', label: 'Smallest drill', hint: 'Below this the hole cannot be made.' },
  { key: 'maxDrill', label: 'Largest drill', hint: 'Above this the opening has to be routed.' },
  { key: 'minHoleToHole', label: 'Hole to hole', hint: 'Wall left between two holes.' },
  { key: 'minEdgeClearance', label: 'Copper to edge', hint: 'Copper to the routed board edge.' },
  { key: 'minSilkWidth', label: 'Silkscreen width', hint: 'Narrowest line the screen prints.' },
  { key: 'minSilkHeight', label: 'Silkscreen height', hint: 'Smallest lettering that still reads.' },
];

export function showDfmRules(opts: DfmRulesOptions): void {
  const draft: DfmRules = { ...opts.rules };

  openDialog({
    title: 'Manufacturing rules',
    build(body, handle) {
      const intro = document.createElement('p');
      intro.className = 'modal-hint';
      intro.textContent = 'What the board house can make. Pick a set to start from, '
        + 'then change whatever your shop tells you is different.';
      body.append(intro);

      const form = document.createElement('div');
      form.className = 'modal-form';
      body.append(form);

      const inputs = new Map<keyof DfmRules, HTMLInputElement>();

      // Declared up here because the list and the figure boxes both enable and
      // disable it as they change; it is appended with the rest below.
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'btn';
      del.textContent = 'Delete';
      del.disabled = true;

      // --- The named sets ----------------------------------------------------
      const pickRow = document.createElement('label');
      pickRow.className = 'field';
      const pickLabel = document.createElement('span');
      pickLabel.textContent = 'Start from';
      const pick = document.createElement('select');
      const note = document.createElement('p');
      note.className = 'modal-hint dfm-profile-note';

      // The saved sets live here rather than in the caller so that adding one
      // can refill the list without closing the dialog — saving a set and then
      // being thrown out of the form you saved it from is a poor reward.
      let saved: DfmProfile[] = [...(opts.saved ?? [])];

      const fillList = (select: string): void => {
        pick.replaceChildren();
        const now = document.createElement('option');
        now.value = '';
        now.textContent = 'Whatever is set now';
        pick.append(now);
        for (const p of allProfiles(saved)) {
          const o = document.createElement('option');
          o.value = p.id;
          // Marked, because a set of yours and one that ships are different
          // things: only one of them can be deleted.
          o.textContent = isCustom(p.id) ? `${p.name} (saved)` : p.name;
          pick.append(o);
        }
        pick.value = select;
      };

      /** Which saved set the boxes currently match, if any. */
      const currentSaved = (): DfmProfile | undefined =>
        saved.find((p) => sameRules(p.rules, draft));

      const refreshButtons = (): void => {
        const mine = currentSaved();
        del.disabled = mine === undefined;
        del.title = mine
          ? `Forget "${mine.name}"`
          : 'Only a set you saved can be deleted';
      };

      const fillBoxes = (): void => {
        for (const [key, input] of inputs) {
          input.value = editValue(draft[key], opts.units);
        }
      };

      pick.addEventListener('change', () => {
        const p = allProfiles(saved).find((x) => x.id === pick.value);
        note.textContent = p?.note ?? '';
        if (!p) return;
        Object.assign(draft, p.rules);
        // Fill the boxes rather than close the dialog: the set is a starting
        // point and the next thing anyone does is change one of the nine.
        fillBoxes();
        refreshButtons();
      });
      pickRow.append(pickLabel, pick);
      form.append(pickRow, note);

      // --- The nine figures --------------------------------------------------
      for (const r of ROWS) {
        const wrap = document.createElement('label');
        wrap.className = 'field edit-field';
        const span = document.createElement('span');
        span.textContent = r.label;
        const input = document.createElement('input');
        input.type = 'text';
        input.value = editValue(draft[r.key], opts.units);
        input.title = r.hint;
        const asOpened = draft[r.key];
        input.addEventListener('input', () => {
          const v = readEdit(input.value, opts.units, asOpened);
          if (v !== null && v >= 0) draft[r.key] = v;
          // Changing a figure means these are no longer the saved set, so
          // Delete has to stop offering to remove it.
          refreshButtons();
        });
        const unit = document.createElement('span');
        unit.className = 'field-unit';
        unit.textContent = opts.units;
        wrap.append(span, input, unit);
        form.append(wrap);
        inputs.set(r.key, input);
      }

      const row = buttons(body);
      button(row, 'Check', () => {
        opts.onApply({ ...draft });
        handle.close();
      }, true);

      if (opts.onSaveProfile) {
        button(row, 'Save as…', () => {
          const ask = opts.askName ?? ((s2: string) => window.prompt('Name this set of rules:', s2));
          const name = ask(currentSaved()?.name ?? 'My board house');
          if (name === null || name.trim() === '') return;
          const made = customProfile(name, draft);
          // Saving over a set of the same name replaces it rather than making a
          // second one you cannot tell apart in the list.
          saved = [...saved.filter((x) => x.id !== made.id), made];
          opts.onSaveProfile?.(made);
          fillList(made.id);
          note.textContent = made.note;
          refreshButtons();
        });
      }

      button(row, 'Reset', () => {
        Object.assign(draft, DEFAULT_DFM);
        fillBoxes();
        fillList('standard');
        note.textContent = DFM_PROFILES[0]?.note ?? '';
        refreshButtons();
      });
      if (opts.onDeleteProfile) {
        del.addEventListener('click', () => {
          const mine = currentSaved();
          if (!mine) return;
          saved = saved.filter((x) => x.id !== mine.id);
          opts.onDeleteProfile?.(mine.id);
          fillList('');
          note.textContent = '';
          refreshButtons();
        });
        row.append(del);
      }

      button(row, 'Cancel', () => handle.close());

      // Now that the boxes exist, put the list and the buttons in the state the
      // figures actually describe: a set matching what is loaded shows as that
      // set, so Delete offers to remove the right one.
      fillList(currentSaved()?.id ?? '');
      note.textContent = currentSaved()?.note ?? '';
      refreshButtons();
      handle.setAccept(() => {
        opts.onApply({ ...draft });
        handle.close();
      });
    },
  });
}
